import type { Ref } from 'vue'
import { useMobileAppData, type MobileAppData } from './mobile-app-data'

const DEVICE_CREATED = 'DEVICE_CREATED'
const SHOW_QR_CODE = 'SHOW_QR_CODE'
const SIGN_OUT = 'SIGN_OUT'
const MOBILE_ACCESS_TOKEN_UNAUTHORIZED = 'MOBILE_ACCESS_TOKEN_UNAUTHORIZED'
const SHOW_ABOUT_PAGE = 'SHOW_ABOUT_PAGE'
const READY_FOR_DEEP_LINK = 'READY_FOR_DEEP_LINK'
const INITIAL_NAV_COMPLETE = 'INITIAL_NAV_COMPLETE'
const SHOW_PUSH_SETTINGS = 'SHOW_PUSH_SETTINGS'

type MobileAppMessage = {
    action: string
    url?: string
    deviceKey?: string
    deviceToken?: string
    qrCustomers?: { id: string; commaName: string }[]
    qrCredential?: {
        id: string
        organizationId: string
    } | null
}

function postMessageCapacitor(
    mobileAppData: Ref<MobileAppData>,
    message: MobileAppMessage
) {
    if (mobileAppData.value.capacitorOrigin === null) {
        alert('Oops! We are having internal communication troubles')
        return
    }

    // Only post to known capacitor origin
    window.parent.postMessage(message, mobileAppData.value.capacitorOrigin)
}

export function usePostMessageCapacitor() {
    const mobileAppData = useMobileAppData()

    function postDeviceCreated(
        deviceKey: string,
        qrCustomers: NonNullable<MobileAppMessage['qrCustomers']>,
        qrCredential: NonNullable<MobileAppMessage['qrCredential']> | null,
        deviceToken: string
    ) {
        // Profile creation finishes in CPX, but the private signing key stays
        // native. Return only the server credential and eligible customer list.
        postMessageCapacitor(mobileAppData, {
            action: DEVICE_CREATED,
            deviceKey: deviceKey,
            deviceToken: deviceToken,
            qrCustomers: qrCustomers,
            qrCredential: qrCredential
        })
    }

    function postShowQrCode() {
        postMessageCapacitor(mobileAppData, {
            action: SHOW_QR_CODE
        })
    }

    function postSignOut() {
        postMessageCapacitor(mobileAppData, {
            action: SIGN_OUT
        })
    }

    function postShowAboutPage() {
        postMessageCapacitor(mobileAppData, {
            action: SHOW_ABOUT_PAGE
        })
    }

    function postMobileAccessTokenUnauthorized() {
        postMessageCapacitor(mobileAppData, {
            action: MOBILE_ACCESS_TOKEN_UNAUTHORIZED
        })
    }

    function postReadyForDeepLink() {
        postMessageCapacitor(mobileAppData, {
            action: READY_FOR_DEEP_LINK
        })
    }

    /**
     * Tell the native shell the initial destination is ready so it can dismiss the splash and startup timeout.
     */
    function postInitialNavigationComplete() {
        postMessageCapacitor(mobileAppData, {
            action: INITIAL_NAV_COMPLETE
        })
    }

    /** Ask the native shell to open a website, email composer or phone dialer. */
    function postOpenExternalUrl(url: string) {
        postMessageCapacitor(mobileAppData, {
            action: 'OPEN_EXTERNAL_URL',
            url
        })
    }

    function postShowPushNotificationSettings() {
        postMessageCapacitor(mobileAppData, {
            action: SHOW_PUSH_SETTINGS
        })
    }

    return {
        postInitialNavigationComplete,
        postOpenExternalUrl,
        postDeviceCreated,
        postMobileAccessTokenUnauthorized,
        postReadyForDeepLink,
        postShowAboutPage,
        postShowQrCode,
        postShowPushNotificationSettings,
        postSignOut
    }
}
